import React, { useState, useEffect, useCallback } from 'react';
import { useBridgeRouteOptions, edgeLinkQueryKeys } from '@/lib/api/edgelink';
import { BridgeRouteOption } from '@/lib/api/edgelink/types';
import { ConnectionType } from '@/lib/api/types.gen';
import { cn } from '@/lib/utils';
import { useQueryClient } from '@tanstack/react-query';
import { ConnectEdgeLinkModal } from './ConnectEdgeLinkModal';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Check,
  ChevronsUpDown,
  Network,
  Cloud,
  Shield,
  Route,
  X,
  Plus,
  RefreshCw,
  ExternalLink,
} from 'lucide-react';

function getConnectionIcon(type: ConnectionType) {
  switch (type) {
    case ConnectionType.Cloudflared:
      return Cloud;
    case ConnectionType.CloudflareIpsec:
      return Shield;
    default:
      return Network;
  }
}

function getConnectionLabel(type: ConnectionType) {
  switch (type) {
    case ConnectionType.Cloudflared:
      return 'Tunnel';
    case ConnectionType.CloudflareIpsec:
      return 'IPSec';
    default:
      return type;
  }
}

interface BridgeRouteSelectorProps {
  value?: string | null;
  onChange: (routeId: string | null) => void;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
}

export default function BridgeRouteSelector({
  value,
  onChange,
  className,
  disabled,
  placeholder = 'Select a bridge route...',
}: BridgeRouteSelectorProps) {
  const [open, setOpen] = useState(false);
  const [connectModalOpen, setConnectModalOpen] = useState(false);
  const queryClient = useQueryClient();
  const { data: routes, isLoading, isError, refetch, isFetching } = useBridgeRouteOptions();

  const handleConnectModalComplete = useCallback(
    (routeId: string) => {
      setConnectModalOpen(false);
      // Refresh routes to include the new bridge
      queryClient.invalidateQueries({ queryKey: edgeLinkQueryKeys.routes() });
      // Select the new route
      onChange(routeId);
    },
    [onChange, queryClient]
  );

  // Refresh data when tab becomes visible (user might have created a bridge in another tab)
  const handleVisibilityChange = useCallback(() => {
    if (document.visibilityState === 'visible') {
      queryClient.invalidateQueries({ queryKey: edgeLinkQueryKeys.routes() });
    }
  }, [queryClient]);

  useEffect(() => {
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [handleVisibilityChange]);

  // Also refresh when popover opens
  const handleOpenChange = (newOpen: boolean) => {
    if (newOpen) {
      refetch();
    }
    setOpen(newOpen);
  };

  // Find the selected route
  const selectedRoute = routes?.find((r) => r.routeId === value);

  // Group routes by bridge
  const groupedRoutes = React.useMemo(() => {
    if (!routes) return new Map<string, { bridge: { id: string; name: string; type: ConnectionType }; routes: BridgeRouteOption[] }>();
    
    const groups = new Map<string, { bridge: { id: string; name: string; type: ConnectionType }; routes: BridgeRouteOption[] }>();
    
    for (const route of routes) {
      const existing = groups.get(route.bridgeId);
      if (existing) {
        existing.routes.push(route);
      } else {
        groups.set(route.bridgeId, {
          bridge: {
            id: route.bridgeId,
            name: route.bridgeName,
            type: route.bridgeType,
          },
          routes: [route],
        });
      }
    }
    
    return groups;
  }, [routes]);

  const handleClear = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    onChange(null);
  };

  if (isLoading) {
    return (
      <div className={cn('space-y-2', className)}>
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className={cn('flex items-center gap-2 text-destructive text-sm', className)}>
        <Network className="h-4 w-4" />
        <span>Failed to load EdgeLink routes</span>
      </div>
    );
  }

  return (
    <div className={cn('w-full space-y-2', className)}>
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild disabled={disabled}>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              'w-full justify-between font-normal h-auto py-2',
              !selectedRoute && 'text-muted-foreground'
            )}
            >
            {selectedRoute ? (
              <div className="flex flex-row items-center gap-3 min-w-0">
                <div>
                  {React.createElement(getConnectionIcon(selectedRoute.bridgeType), {
                    className: 'h-4 w-4 shrink-0 text-muted-foreground',
                  })}
                </div>
                <div className="flex items-start gap-2 text-left min-w-0 flex-1">
                  <span className="font-semibold truncate">{selectedRoute.bridgeName}</span>
                  <Badge variant="outline" className="shrink-0 px-1 h-5">
                    {getConnectionLabel(selectedRoute.bridgeType)}
                  </Badge>
                  <span className="font-mono text-muted-foreground">{selectedRoute.routeAddressRange}</span>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <Network className="h-4 w-4" />
                <span>{placeholder}</span>
              </div>
            )}
            <div className="flex items-center gap-1 shrink-0 ml-2">
              {selectedRoute && (
                <>
                  <a
                    href={`/edgelink/${selectedRoute.bridgeId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-full p-0.5 hover:bg-lime/10 text-muted-foreground hover:text-lime transition-colors"
                    onClick={(e) => e.stopPropagation()}
                    title="View EdgeLink bridge"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label="Clear selection"
                    className="h-5 w-5 rounded-full hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                    onClick={handleClear}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </>
              )}
              <ChevronsUpDown className="h-4 w-4 opacity-50" />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[400px] p-0" align="start">
          <Command>
            <div className="relative">
              <CommandInput placeholder="Search bridges..." />
              {isFetching && (
                <div className="absolute right-3 top-0 bottom-0 flex items-center">
                  <RefreshCw className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                </div>
              )}
            </div>
            <CommandList>
              <CommandEmpty>
                <div className="py-6 text-center">
                  <Network className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground mb-3">No EdgeLink bridges found</p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    onClick={() => {
                      setOpen(false);
                      setConnectModalOpen(true);
                    }}
                  >
                    <Plus className="h-3 w-3" />
                    Create EdgeLink Bridge
                  </Button>
                </div>
              </CommandEmpty>
              
              <CommandGroup heading="Available Bridges">
                {Array.from(groupedRoutes.values()).map(({ bridge, routes: bridgeRoutes }) => {
                  const Icon = getConnectionIcon(bridge.type);
                  // Check if the current selected route belongs to this bridge
                  const isBridgeSelected = selectedRoute?.bridgeId === bridge.id;
                  
                  return (
                    <CommandItem
                    key={bridge.id}
                    value={`${bridge.name} ${bridge.type}`}
                    onSelect={() => {
                      // If clicking the already selected bridge, just close (or maybe we want to allow re-selecting to reset to first route? Let's just select first route)
                      // Auto-select the first route
                      if (bridgeRoutes.length > 0) {
                        onChange(bridgeRoutes[0].routeId);
                      }
                      setOpen(false);
                    }}
                    className={`flex items-center justify-between py-2 ${isBridgeSelected ? 'bg-lime/10' : ''}`}
                    >
                      <div className="flex items-center gap-2">
                        <Icon className={cn("h-4 w-4", isBridgeSelected ? "" : "text-muted-foreground")} />
                        <div className="flex flex-col">
                          <span className="font-medium">{bridge.name}</span>
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            {bridgeRoutes.length} route{bridgeRoutes.length !== 1 ? 's' : ''} • {getConnectionLabel(bridge.type)}
                          </span>
                        </div>
                      </div>
                      {isBridgeSelected && <Check className="h-4 w-4 text-lime" />}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
            
            <CommandSeparator />
            <CommandGroup>
              <CommandItem
                onSelect={() => {
                  setOpen(false);
                  setConnectModalOpen(true);
                }}
                className="gap-2"
              >
                <Plus className="h-4 w-4" />
                <span>Create new EdgeLink bridge</span>
              </CommandItem>
            </CommandGroup>

          </Command>
        </PopoverContent>
      </Popover>
      
      {/* Route Refinement - Only shown if a bridge is selected and has multiple routes */}
      <div className="pl-1">
          {(() => {
            if (!selectedRoute) {
              return <></>
            }

            const bridgeGroup = groupedRoutes.get(selectedRoute.bridgeId);
            if (!bridgeGroup || bridgeGroup.routes.length <= 1) return null;
            
            return (
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                  Route Selection
                </div>
                <div className="flex flex-wrap gap-2">
                  {bridgeGroup.routes.map((route) => (
                    <div
                    key={route.routeId}
                    onClick={() => onChange(route.routeId)}
                    className={cn(
                      "cursor-pointer rounded-md border px-3 py-1.5 text-xs font-mono transition-colors flex items-center gap-2",
                      selectedRoute.routeId === route.routeId 
                      ? "bg-card2/50 border-lime hover:bg-card2/100" 
                      : "bg-background hover:bg-muted text-muted-foreground"
                    )}
                    >
                      <div className={cn("w-1.5 h-1.5 rounded-full", selectedRoute.routeId === route.routeId ? "bg-lime" : "bg-muted-foreground/30")} />
                      {route.routeAddressRange}
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
      </div>

      {/* Connect EdgeLink Modal */}
      <ConnectEdgeLinkModal
        open={connectModalOpen}
        onOpenChange={setConnectModalOpen}
        onComplete={handleConnectModalComplete}
      />
  </div>
  );
}
